import { useSearchParams } from "react-router";

import type { BundlePageProps } from "./product-detail-page-layout";

import { getDefaultVariantsSelection } from "./getDefaultVariantsSelection";

import type { BundleVariantSelection } from "./BundleVariantSelection";
import type { BundlePDPVariant } from "./BundleProduct";
import type { EnrichedSwarovskiProductVariantFrontend } from "@swo/storefront";
import { useEffect } from "react";
import { pipe } from "fp-ts/lib/function";
import { nonEmptyArray as NEA } from "fp-ts";
import type { ProductVariantAvailabilityStatus } from "@swo/shared";
import isEqual from "react-fast-compare";

export function useCurrentVariantsSelection(
  selectedProduct: BundlePageProps["data"]["selectedProduct"],
  avalabilityMap: Map<string, ProductVariantAvailabilityStatus> | "shopping-not-possible",
) {
  const defaultVariantsSelection: BundleVariantSelection = getDefaultVariantsSelection(selectedProduct, avalabilityMap);

  const [searchParams, setSearchParams] = useSearchParams();

  const currentVariantsState: BundleVariantSelection = pipe(
    selectedProduct.products,
    NEA.map(p => {
      const key = p.key;

      const searchParamVariantKey = searchParams.get(key);

      const selected = searchParamVariantKey
        ? { tag: "key" as const, value: searchParamVariantKey }
        : p.preselectedVariant
          ? { tag: "sku" as const, value: p.preselectedVariant.sku }
          : undefined;

      const fallbackVariant: EnrichedSwarovskiProductVariantFrontend = p.variants[0];
      const value: EnrichedSwarovskiProductVariantFrontend = selected
        ? (p.variants.find(v => {
            switch (selected.tag) {
              case "key":
                return v.key === selected.value;
              case "sku":
                return v.sku === selected.value;
            }
          }) ?? fallbackVariant)
        : fallbackVariant;

      const { attributes, ...rest } = value;

      if (!attributes) {
        throw Error("Cannot use bundle product without attributes");
      }

      const variant: BundlePDPVariant = {
        ...rest,
        attributes,
        availability:
          typeof avalabilityMap !== "string" && avalabilityMap.get(value.sku)
            ? (avalabilityMap.get(value.sku) ?? { tag: "out-of-stock" })
            : { tag: "out-of-stock" },
        product: {
          name: p.name,
          images: p.images,
          shortDescription: p.shortDescription,
        },
      };

      return { key, value: variant };
    }),
  );

  useEffect(() => {
    if (!isEqual(currentVariantsState, defaultVariantsSelection)) {
      setSearchParams(
        prev => {
          currentVariantsState.forEach(({ key, value }) => {
            prev.set(key, value.key);
          });

          return prev;
        },
        {
          preventScrollReset: true,
        },
      );
    } else {
      //
    }
  }, [searchParams, { state: currentVariantsState, default: defaultVariantsSelection }]);

  function onBundleElementChanged({ productKey, variantKey }: { productKey: string; variantKey: string }) {
    setSearchParams(
      prev => {
        prev.set(productKey, variantKey);

        return prev;
      },
      {
        preventScrollReset: true,
        viewTransition: true,
      },
    );
  }

  return {
    state: currentVariantsState,
    onBundleElementChanged,
  };
}
